import Icon from '@/components/ui/AppIcon';

interface TierFeature {
  name: string;
  included: boolean;
}

interface Tier {
  name: string;
  description: string;
  price: string;
  priceDetail: string;
  icon: string;
  features: TierFeature[];
  cta: string;
  highlighted?: boolean;
}

interface PartnershipTiersProps {
  onSelectTier: (tierName: string) => void;
}

export default function PartnershipTiers({ onSelectTier }: PartnershipTiersProps) {
  const tiers: Tier[] = [
    {
      name: "Starter",
      description: "Perfect for travel agencies getting started with API integration",
      price: "$299",
      priceDetail: "per month",
      icon: "RocketLaunchIcon",
      features: [
        { name: "Up to 1,000 API calls/month", included: true },
        { name: "Basic PNR management", included: true },
        { name: "Email support (48h response)", included: true },
        { name: "Standard documentation access", included: true },
        { name: "5% revenue share", included: true },
        { name: "Sandbox environment", included: true },
        { name: "Priority support", included: false },
        { name: "Custom integrations", included: false },
        { name: "White-label solutions", included: false },
      ],
      cta: "Start Free Trial"
    },
    {
      name: "Professional",
      description: "Advanced features for growing travel businesses",
      price: "$799",
      priceDetail: "per month",
      icon: "BuildingOfficeIcon",
      features: [
        { name: "Up to 10,000 API calls/month", included: true },
        { name: "Advanced PNR management", included: true },
        { name: "Priority support (12h response)", included: true },
        { name: "Full API documentation", included: true },
        { name: "8% revenue share", included: true },
        { name: "Sandbox + staging environments", included: true },
        { name: "Webhook integrations", included: true },
        { name: "Custom branding options", included: true },
        { name: "Dedicated account manager", included: false },
      ],
      cta: "Get Started",
      highlighted: true
    },
    {
      name: "Enterprise",
      description: "Complete solution for airlines and large travel corporations",
      price: "Custom",
      priceDetail: "contact sales",
      icon: "GlobeAltIcon",
      features: [
        { name: "Unlimited API calls", included: true },
        { name: "Enterprise PNR management", included: true },
        { name: "24/7 dedicated support", included: true },
        { name: "Custom API development", included: true },
        { name: "12% revenue share", included: true },
        { name: "All environments + production", included: true },
        { name: "Advanced webhook system", included: true },
        { name: "Full white-label solution", included: true },
        { name: "Dedicated technical team", included: true },
      ],
      cta: "Contact Sales"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="CurrencyDollarIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">Flexible Partnership Models</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Choose Your Integration Tier
          </h2>
          <p className="text-lg text-muted-foreground">
            Scale with confidence. Start small and upgrade as your business grows. All tiers include our core PNR guarantee.
          </p>
        </div>

        {/* Tiers Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`relative bg-card rounded-2xl shadow-brand hover:shadow-brand-lg transition-all duration-300 ${
                tier.highlighted ? 'ring-2 ring-primary scale-105 lg:scale-110' : ''
              }`}
            >
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-white px-6 py-2 rounded-full text-sm font-heading font-semibold shadow-lg">
                  Most Popular
                </div>
              )}

              <div className="p-8">
                {/* Tier Header */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-3 rounded-lg ${tier.highlighted ? 'bg-primary' : 'bg-surface'}`}>
                    <Icon 
                      name={tier.icon as any} 
                      size={28} 
                      className={tier.highlighted ? 'text-white' : 'text-primary'} 
                    />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold text-foreground">{tier.name}</h3>
                  </div>
                </div>

                <p className="text-muted-foreground mb-6">{tier.description}</p>

                {/* Pricing */}
                <div className="mb-8">
                  <div className="flex items-baseline space-x-2">
                    <span className="font-heading text-4xl font-bold text-foreground">{tier.price}</span>
                    <span className="text-muted-foreground">{tier.priceDetail}</span>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => onSelectTier(tier.name)}
                  className={`w-full py-3 px-6 rounded-lg font-heading font-semibold transition-all duration-300 mb-8 ${
                    tier.highlighted
                      ? 'bg-primary hover:bg-secondary text-white shadow-md hover:shadow-lg'
                      : 'bg-surface hover:bg-muted text-foreground'
                  }`}
                >
                  {tier.cta}
                </button>

                {/* Features List */}
                <div className="space-y-4">
                  <div className="text-sm font-heading font-semibold text-foreground mb-3">
                    What's included:
                  </div>
                  {tier.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon
                        name={feature.included ? 'CheckCircleIcon' : 'XCircleIcon'}
                        size={20}
                        className={feature.included ? 'text-success mt-0.5' : 'text-muted-foreground/40 mt-0.5'}
                      />
                      <span className={`text-sm ${feature.included ? 'text-foreground' : 'text-muted-foreground/60'}`}>
                        {feature.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground">
            All plans include our 99.9% uptime SLA and PNR guarantee. Need a custom solution?{' '}
            <button
              onClick={() => onSelectTier('Custom')}
              className="text-primary hover:text-secondary font-medium underline"
            >
              Contact our enterprise team
            </button>
          </p>
        </div>
      </div>
    </section>
  );
}